import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useCartStore } from '@/store/cartStore'
import { useOrderStore } from '@/store/orderStore'
import { processPayment, PaymentMethod, CheckoutData } from '@/services/payment/paymentService'
import { Address } from '@/types/order'
import { AddressFormData } from '@/components/shop/AddressForm'
import AddressForm from '@/components/shop/AddressForm'
import PaymentForm from '@/components/shop/PaymentForm'
import toast from 'react-hot-toast'
import { FaShoppingCart, FaMapMarkerAlt, FaCreditCard } from 'react-icons/fa'

const Checkout = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { items, getTotalPrice, clearCart } = useCartStore()
  const { addOrder } = useOrderStore()

  const [email, setEmail] = useState('')
  const [deliveryAddress, setDeliveryAddress] = useState<Address | null>(null)
  const [invoiceAddress, setInvoiceAddress] = useState<Address | null>(null)
  const [sameAsDelivery, setSameAsDelivery] = useState(true)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const totalPrice = getTotalPrice()
  const shippingCost = 5.99 // TODO: Calculate based on address
  const finalTotal = totalPrice + shippingCost

  const handleDeliveryAddressSubmit = (data: AddressFormData) => {
    setDeliveryAddress(data)
    if (sameAsDelivery) {
      setInvoiceAddress(data)
    }
  }

  const handleInvoiceAddressSubmit = (data: AddressFormData) => {
    setInvoiceAddress(data)
  }

  const handleSameAsDeliveryChange = (checked: boolean) => {
    setSameAsDelivery(checked)
    if (checked && deliveryAddress) {
      setInvoiceAddress(deliveryAddress)
    }
  }

  const handlePlaceOrder = async () => {
    // Validation
    if (!email) {
      toast.error(t('checkout.email') + ' ' + t('validation.required'))
      return
    }

    if (!deliveryAddress) {
      toast.error(t('checkout.deliveryAddress') + ' ' + t('validation.required'))
      return
    }

    if (!invoiceAddress) {
      toast.error(t('checkout.invoiceAddress') + ' ' + t('validation.required'))
      return
    }

    if (!selectedPaymentMethod) {
      toast.error(t('checkout.paymentMethod') + ' ' + t('validation.required'))
      return
    }

    if (items.length === 0) {
      toast.error(t('cart.empty'))
      navigate('/shop')
      return
    }

    setIsProcessing(true)

    try {
      const checkoutData: CheckoutData = {
        email,
        deliveryAddress,
        invoiceAddress,
        paymentMethod: selectedPaymentMethod,
        items,
        totalPrice: finalTotal,
      }

      const result = await processPayment(checkoutData)

      if (result.success && result.order) {
        // Add order to store
        addOrder(result.order)

        // Clear cart
        clearCart()

        // Show success message
        toast.success(t('checkout.orderSuccess'))

        // Navigate to orders page
        navigate(`/orders?orderId=${result.order.id}`)
      } else {
        toast.error(result.error || t('checkout.orderError'))
      }
    } catch (error: any) {
      console.error('Checkout error:', error)
      toast.error(error.message || t('checkout.orderError'))
    } finally {
      setIsProcessing(false)
    }
  }

  if (items.length === 0) {
    return (
      <div className="bg-dark-950 min-h-screen py-12 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <FaShoppingCart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-300 mb-2">{t('cart.empty')}</h2>
          <button
            onClick={() => navigate('/shop')}
            className="mt-4 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold transition-colors"
          >
            {t('cart.continueShopping')}
          </button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="bg-dark-950 min-h-screen py-6 sm:py-8 relative overflow-hidden">
      <div className="fixed inset-0 bg-grid-pattern opacity-20"></div>
      <div className="absolute inset-0 bg-gradient-mesh opacity-30"></div>

      <div className="container-custom px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-8"
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black mb-3 sm:mb-4">
            <span className="text-gradient bg-clip-text text-transparent bg-gradient-to-r from-primary-400 via-purple-400 to-primary-400">
              {t('checkout.title')}
            </span>
          </h1>
          <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mt-4"></div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Email */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card-hover p-6"
            >
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('checkout.email')} *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('checkout.emailPlaceholder')}
              />
            </motion.div>

            {/* Delivery Address */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card-hover p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <FaMapMarkerAlt className="w-5 h-5 text-primary-400" />
                <h2 className="text-xl font-bold text-gray-100">{t('checkout.deliveryAddress')}</h2>
              </div>
              <AddressForm
                onSubmit={handleDeliveryAddressSubmit}
                defaultValues={deliveryAddress || undefined}
              />
            </motion.div>

            {/* Invoice Address */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card-hover p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FaMapMarkerAlt className="w-5 h-5 text-primary-400" />
                  <h2 className="text-xl font-bold text-gray-100">{t('checkout.invoiceAddress')}</h2>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sameAsDelivery}
                    onChange={(e) => handleSameAsDeliveryChange(e.target.checked)}
                    className="w-4 h-4 text-primary-500 bg-dark-800 border-dark-700 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-400">{t('checkout.sameAsDelivery')}</span>
                </label>
              </div>
              {sameAsDelivery ? (
                <p className="text-gray-400 text-sm">{t('checkout.sameAsDelivery')}</p>
              ) : (
                <AddressForm
                  onSubmit={handleInvoiceAddressSubmit}
                  defaultValues={invoiceAddress || undefined}
                />
              )}
            </motion.div>

            {/* Payment Method */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="card-hover p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <FaCreditCard className="w-5 h-5 text-primary-400" />
                <h2 className="text-xl font-bold text-gray-100">{t('checkout.paymentMethod')}</h2>
              </div>
              <PaymentForm
                selectedMethod={selectedPaymentMethod}
                onMethodSelect={setSelectedPaymentMethod}
              />
            </motion.div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="card-hover p-6 sticky top-24"
            >
              <h2 className="text-xl font-bold text-gray-100 mb-6">{t('checkout.orderSummary')}</h2>

              {/* Items */}
              <div className="space-y-3 mb-6">
                {items.map((item) => (
                  <div key={item.productId} className="flex justify-between text-sm">
                    <span className="text-gray-400">
                      {item.productName} × {item.quantity}
                    </span>
                    <span className="text-gray-200 font-semibold">
                      {(item.price * item.quantity).toFixed(2)} €
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-dark-700 pt-4 space-y-2 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t('cart.subtotal')}</span>
                  <span className="text-gray-200">{totalPrice.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t('cart.shipping')}</span>
                  <span className="text-gray-200">{shippingCost.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-dark-700">
                  <span className="text-gray-100">{t('cart.total')}</span>
                  <span className="text-primary-400">{finalTotal.toFixed(2)} €</span>
                </div>
              </div>

              <button
                onClick={handlePlaceOrder}
                disabled={isProcessing || !deliveryAddress || !invoiceAddress || !selectedPaymentMethod}
                className={`
                  w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300
                  ${
                    isProcessing || !deliveryAddress || !invoiceAddress || !selectedPaymentMethod
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 hover:from-primary-600 hover:via-primary-700 hover:to-primary-800 text-white shadow-lg shadow-primary-500/50'
                  }
                `}
              >
                {isProcessing ? t('checkout.processing') : t('checkout.placeOrder')}
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Checkout
